import { FunctionComponent, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import { faPlusMinus } from '@fortawesome/pro-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { InventoryMovement, Lot, Product } from '@spike/product-model';
import { fetchMovementsThunk, ProductsStatus } from '@spike/products-action';
import { RootState } from 'store';
import { useApiClientWrapper } from 'hooks';
import { Spinner } from 'components/UI';
import Pagination from 'components/UI/Pagination';
import Button from 'components/UI/V3/Button/Button';
import { ProductHeader } from '../StockSidebar/ProductHeader';
import { getMovementReason } from '../StockSidebar/StockHistory';
import moment from 'moment';
import clsx from 'clsx';
import stock from '../StockSidebar/StockSidebar.module.scss';
import styles from './LotsSidebar.module.scss';

interface LotHistoryProps {
    product: Product;
    lot: Lot;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    onBack: () => void;
    onAdjust: () => void;
}

const formatHistoryDate = (value?: moment.Moment | string | null): string => {
    if (!value) {
        return '-';
    }

    const date = moment.isMoment(value) ? value : moment(value);
    if (!date.isValid()) {
        return '-';
    }

    return date.format('MMM D, YYYY · h:mma').replace('AM', 'am').replace('PM', 'pm');
};

const formatTableDate = (value?: moment.Moment | string | null): string => {
    if (!value) {
        return '-';
    }

    const date = moment.isMoment(value) ? value : moment(value);
    if (!date.isValid()) {
        return '-';
    }

    return date.format('MMM D, YYYY h:mma').replace('AM', 'am').replace('PM', 'pm');
};

const formatExpirationDate = (expirationDate?: string | null): string => {
    if (!expirationDate) {
        return '-';
    }

    const date = moment(expirationDate);
    return date.isValid() ? date.format('MMM D, YYYY') : '-';
};

const formatUnitCost = (value: number | undefined): string => {
    if (value === undefined || Number.isNaN(Number(value))) {
        return '-';
    }

    return `$${Number(value).toFixed(2)}`;
};

const formatDelta = (delta: number): string => {
    if (delta > 0) {
        return `+${delta}`;
    }

    return String(delta);
};

export const LotHistory: FunctionComponent<LotHistoryProps> = ({
    product,
    lot,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    onBack,
    onAdjust
}) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const movements = useSelector<RootState, Array<InventoryMovement>>(state => state.products.movements.page ?? []);
    const totalRecords = useSelector<RootState, number>(
        state => state.products.movements.pagination?.totalRecords ?? 0
    );
    const status = useSelector<RootState, ProductsStatus>(state => state.products.status);
    const loading = status === ProductsStatus.FetchingMovements;
    const businessProductId = product.businessProduct?.id;
    const firstMovement = movements[0];

    useEffect(() => {
        if (businessProductId == null || lot.id == null) {
            return;
        }

        dispatch(
            fetchMovementsThunk(
                apiClientWrapper,
                businessProductId,
                lot.id,
                [],
                '',
                undefined,
                undefined,
                page,
                pageSize
            )
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessProductId, lot.id, page, pageSize]);

    return (
        <>
            <div className={styles.historyHeader}>
                <button
                    type="button"
                    className={stock.backButton}
                    onClick={onBack}
                    aria-label="Back to lots"
                >
                    <FontAwesomeIcon icon={faArrowLeft} />
                </button>
                <h2 className={stock.detailsTitle}>Lot {lot.lotNumber || ''}</h2>
                <Button
                    label="Adjust Lot"
                    size="medium"
                    variant="primary"
                    leftIcon={faPlusMinus}
                    onClick={onAdjust}
                />
            </div>
            <ProductHeader
                product={product}
                trackingLabel="Track by Lots"
            />
            <div className={stock.metrics}>
                <div className={stock.metricCard}>
                    <span className={stock.metricLabel}>Quantity</span>
                    <span className={stock.metricValue}>{lot.quantity}</span>
                </div>
            </div>
            <div className={stock.detailsList}>
                <div className={stock.detailRow}>
                    <span className={stock.detailLabel}>Date</span>
                    <span className={stock.detailValue}>{formatHistoryDate(lot.createdAt)}</span>
                </div>
                <div className={stock.detailRow}>
                    <span className={stock.detailLabel}>Staff</span>
                    <span className={stock.detailValue}>{firstMovement?.staff?.name || '-'}</span>
                </div>
                <div className={stock.detailRow}>
                    <span className={stock.detailLabel}>Expires</span>
                    <span className={stock.detailValue}>{formatExpirationDate(lot.expirationDate)}</span>
                </div>
                <div className={stock.detailRow}>
                    <span className={stock.detailLabel}>Unit Cost</span>
                    <span className={stock.detailValue}>{formatUnitCost(lot.unit_cost)}</span>
                </div>
            </div>
            <div className={styles.detailsDivider} />
            <h3 className={stock.historySectionTitle}>Lot History</h3>
            {loading ? (
                <div className={stock.spinnerWrap}>
                    <Spinner />
                </div>
            ) : (
                <>
                    <div className={styles.historyTable}>
                        <div className={styles.historyTableHead}>
                            <span>Date</span>
                            <span>Reason</span>
                            <span>Adjust Qty</span>
                            <span>Lot Qty</span>
                        </div>
                        {movements.map(movement => (
                            <div
                                key={movement.uuid || movement.id}
                                className={styles.historyTableRow}
                            >
                                <div className={styles.historyDateCell}>
                                    <span>{formatTableDate(movement.createdAt)}</span>
                                    <span className={styles.byStaff}>By {movement.staff?.name || '-'}</span>
                                </div>
                                <span>{getMovementReason(movement) || '-'}</span>
                                <span
                                    className={clsx(
                                        styles.delta,
                                        movement.quantity.delta < 0 ? styles.deltaNegative : styles.deltaPositive
                                    )}
                                >
                                    {formatDelta(movement.quantity.delta)}
                                </span>
                                <span>{movement.quantity.after.lot}</span>
                            </div>
                        ))}
                    </div>
                    {totalRecords > 0 && (
                        <Pagination
                            className={styles.pagination}
                            count={totalRecords}
                            page={page}
                            pageSize={pageSize}
                            onPageChange={onPageChange}
                            onPageSizeChange={onPageSizeChange}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default LotHistory;
